import { isPlatform } from '@ionic/react';
import parse from 'html-react-parser';
import { markdown } from './markdown';
import dayjs from 'dayjs';

const FALLBACK_LOCALE = 'es-MX';

// Mutable so the active locale can react to the tenant's company.country
// (set via setNumberLocale) instead of being fixed at es-MX for every account.
//
// This is invisible to React: it's a plain module-level variable, not state
// or context. formatDecimalNumber/formatCompactNumber calls made directly in
// a render body pick up the current value correctly, but a value cached in a
// useMemo with only data-dependencies (chart options, column defs, etc.)
// keeps whatever locale was active when that memo last ran — if
// company.country resolves AFTER that memo's first computation, it stays
// stale until something else invalidates it. In practice the risk is low:
// company (and its country) is set once, early in a session, and getting
// this fully reactive would mean threading locale through the ~190
// formatDecimalNumber/formatCompactNumber call sites as an explicit
// prop/context value — the exact scope this module-global approach was
// chosen to avoid. If a specific memo is ever found to actually be at risk
// (rendered before company resolves, unlikely to re-run for any other
// reason), getNumberLocale() is defined below to be re-exported from
// ui-lib's index for exactly that — it isn't today, since nothing needs it.
let currentNumberLocale: string = FALLBACK_LOCALE;

export const setNumberLocale = (locale: string | undefined | null): void => {
  currentNumberLocale = locale || FALLBACK_LOCALE;
};

export const getNumberLocale = (): string => currentNumberLocale;

// Explicit allowlist instead of a generic `es-${country}` pattern: a
// structurally-valid-but-wrong tag like es-FR or es-DE would otherwise pass
// Intl's own validation and silently fall back to generic `es` root data —
// which isn't necessarily what that country's client actually expects. Every
// country a real tenant is on today (per company-service) is listed here;
// anything else deliberately falls back rather than guessing.
// BE is deliberately mapped to the fallback: fr-BE (space thousands
// separator) and nl-BE (dot thousands separator) disagree with each other,
// so we hold off until the client confirms which convention to use.
const COUNTRY_LOCALE_MAP: Record<string, string> = {
  US: 'es-US',
  MX: 'es-MX',
  CL: 'es-CL',
  PE: 'es-PE',
  HN: 'es-HN',
  EC: 'es-EC',
  BR: 'pt-BR',
  ES: 'es-ES',
  BE: FALLBACK_LOCALE,
};

// getLocaleForCountry runs render-phase from every applyNumberLocaleForCompany
// call site, including TrendGraph (which re-renders per chart update) — without deduping,
// one tenant with an unrecognized country would log this on every single
// render instead of once. Tracked by normalized value, not just "have we
// warned at all", so a genuinely different bad value still surfaces.
const warnedUnrecognizedCountries = new Set<string>();

export const getLocaleForCountry = (country?: string | null): string => {
  if (!country) {
    return FALLBACK_LOCALE;
  }
  const normalized = country.trim().toUpperCase();
  const mapped = COUNTRY_LOCALE_MAP[normalized];
  if (mapped === undefined) {
    // The map only has 2-letter ISO codes. If company-service ever stores a
    // full name ("Ecuador") or a 3-letter code ("ECU") for a real tenant,
    // this silently no-ops (falls back to es-MX) with no visible error and
    // green tests. This console.warn does NOT reach any monitoring today —
    // there's no Sentry/Datadog RUM/LogRocket wired into this repo, so it
    // only helps someone who happens to have devtools open. It's here so the
    // failure at least leaves a trace to find while debugging, and as a
    // marker for wiring into real error tracking (or app-provider's
    // useTracking) if/when that exists.
    if (!warnedUnrecognizedCountries.has(normalized)) {
      warnedUnrecognizedCountries.add(normalized);
      console.warn(
        `[getLocaleForCountry] Unrecognized country "${country}" — ` +
        `falling back to ${FALLBACK_LOCALE}. ` +
        'Expected a 2-letter ISO code (e.g. "EC", "CL", "MX").'
      );
    }
    return FALLBACK_LOCALE;
  }
  return mapped;
};

// Guards every call site against clobbering a correctly-resolved locale with
// the fallback just because THIS particular render/mount doesn't happen to
// carry `company.country` (e.g. a secondary standalone entry sharing the same
// bundle/module scope as a primary one that already resolved it correctly —
// see TrendGraph, which is exposed from the same federation container as the
// main efficiency app). Only ever applies a real, known locale; never resets.
export const applyNumberLocaleForCompany = (
  company?: { country?: string | null } | null
): void => {
  if (company?.country) {
    setNumberLocale(getLocaleForCountry(company.country));
  }
};

export const getTextWidth = (
  text: string,
  fontSize: number,
  fontFamily = 'Montserrat Alternates'
) => {
  const canvas = document.createElement('canvas');
  const context: any = canvas.getContext('2d');
  context.font = `${fontSize}px ${fontFamily}`;
  const metrics = context.measureText(text);
  canvas.remove();

  return metrics.width;
};

/* eslint-disable max-len */
export const isSmartTV =
  /smart-tv|smarttv|smart tv|googletv|appletv|viera|internet.tv|netcast|nettv|opera tv|hbbtv|pov_tv|kdl|bravia|dlnadoc|roku|ce-html/i.test(
    navigator.userAgent
  );
export const isTablet = isPlatform('tablet');
export const isMobile =
  /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) &&
  !isTablet;

export const formatDate = (date: string, timezone: string, newFormat = 'DD-MM-YYYY HH:mm') =>
  dayjs(date).tz(timezone).format(newFormat);

export const parseLine = (line: string, lineIndex: number) => {
  const hyperlinkPattern = /\[([^\]]+)\]\(([^)]+)\)/g;
  const parts = [];
  let lastIndex = 0;
  let match;

  while ((match = hyperlinkPattern.exec(line)) !== null) {
    const [fullMatch, text, url] = match;
    const startIndex = match.index;

    // Add text before the hyperlink
    if (startIndex > lastIndex) {
      parts.push(
        <span key={`text-${lineIndex}-${lastIndex}`}>{line.substring(lastIndex, startIndex)}</span>
      );
    }

    // Add the hyperlink
    parts.push(
      <a
        key={`link-${lineIndex}-${startIndex}`}
        href={url}
        target="_self"
        rel="noopener noreferrer"
      >
        {text}
      </a>
    );

    lastIndex = startIndex + fullMatch.length;
  }

  // Add any remaining text after the last hyperlink
  if (lastIndex < line.length) {
    parts.push(<span key={`text-${lineIndex}-${lastIndex}`}>{line.substring(lastIndex)}</span>);
  }

  return parts.length > 0 ? parts : line;
};

// DEPRECATED
export const parseGPTTextResponse = (message: string) => {
  const sections = message.split('\n\n').map((section, index) => {
    const lines = section.split('\n').map((line, lineIndex) => {
      if (line.match(/^\d+\./)) {
        return (
          <li key={lineIndex}>{parseLine(line.substring(line.indexOf('.') + 2), lineIndex)}</li>
        );
      } else {
        return <p key={lineIndex}>{parseLine(line, lineIndex)}</p>;
      }
    });

    if (section.trim().match(/^\d+\./)) {
      return <ul key={index}>{lines}</ul>;
    } else {
      return <div key={index}>{lines}</div>;
    }
  });
  return sections;
};

export const parseTextResponse = (message: string) => {
  if (!message || typeof message !== 'string') {
    return '';
  }
  
  const HTMLMessage = markdown.parse(message);

  return parse(HTMLMessage as string);
};

export const getCssVar = (name: string, fallback: string): string => {
  if (typeof window !== 'undefined') {
    const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    return value || fallback;
  }
  return fallback;
};

export function getColorBasedOnThreshold(
  progress: number | null | undefined,
  redThreshold: number | null | undefined,
  greenThreshold: number | null | undefined,
  isBackground = false
): string {
  // If progress is null or undefined, return empty string
  if (progress == null) {
    return '';
  }

  // Case 1: Both thresholds exist and are numbers
  if (typeof redThreshold === 'number' && typeof greenThreshold === 'number') {
    if (progress < redThreshold) return isBackground ? 'error-10' : 'error-main';
    if (progress >= redThreshold && progress < greenThreshold)
      return isBackground ? 'warning-10' : 'warning-main';
    return isBackground ? 'success-10' : 'success-main';
  }

  // Case 2: Only redThreshold exists and is a number
  if (typeof redThreshold === 'number' && greenThreshold == null) {
    return progress < redThreshold
      ? isBackground
        ? 'error-10'
        : 'error-main'
      : isBackground
        ? 'success-10'
        : 'success-main';
  }

  // Case 3: Neither threshold exists or is a valid number
  return '';
}

// Locale-aware replacement for the old comma-only regex. Callers (including
// getCompactNumber in efficiency/scrap) sometimes pass a string with a
// non-numeric suffix already appended (e.g. "1234.57K"), so the numeric
// prefix is parsed out, formatted with the active locale's real separators
// and exact decimal-digit count, then the suffix is reattached untouched.
export const numberFormatWithCommas = (numb: number | string): string => {
  const str = numb.toString();
  const match = str.match(/^(-?\d+(?:\.\d+)?)(\D*)$/);
  if (!match) {
    // Behavior note: a non-matching input is returned untouched. The
    // reachable case is exponential notation (getCompactNumber's raw,
    // sub-1000 path stringifies very small floats as e.g. "1e-7", which this
    // regex doesn't match) — that string was already meaningless before
    // this function touched it (no locale-appropriate separators either
    // way), so passing it through unchanged isn't a regression.
    return str;
  }
  const [, numericPart, suffix] = match;
  // Clamped to 20: Intl.NumberFormat throws RangeError above that on engines
  // predating the ES2023 bump to 100. Reachable for very small floats whose
  // string form has many decimal digits (e.g. via getCompactNumber's raw,
  // sub-1000 path) — no real UI ever needs more than 20 fraction digits.
  const decimals = numericPart.includes('.')
    ? Math.min(numericPart.split('.')[1].length, 20)
    : 0;
  const formatted = new Intl.NumberFormat(currentNumberLocale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(Number(numericPart));
  return `${formatted}${suffix}`;
};

export const formatDecimalNumber = (
  value: number | string,
  options: {
    locale?: string;
    precision?: number;
    style?: 'decimal' | 'percent';
  } = {}
): string => {
  const {
    locale = currentNumberLocale,
    precision = 0,
    style = 'decimal'
  } = options;

  const numberValue = typeof value === 'string' ? Number(value) : value;
  
  if (isNaN(numberValue)) {
    return '0';
  }

  const formatOptions: Intl.NumberFormatOptions = {
    maximumFractionDigits: precision,
    style: style
  };

  return new Intl.NumberFormat(locale, formatOptions).format(numberValue);
};

export const formatVelocityNumber = (value: number | string) => {
  const numberValue = typeof value === 'string' ? Number(value) : value;
  return numberValue >= 10
    ? formatDecimalNumber(numberValue, { precision: 0 })
    : formatDecimalNumber(numberValue, { precision: 2 });
};

export const formatCompactNumber = (
  value: number | string,
  options: { locale?: string; emptyValue?: string } = {}
): string => {
  const { locale = currentNumberLocale, emptyValue = '0' } = options;
  const n = typeof value === 'string' ? Number(value) : value;
  if (isNaN(n)) return emptyValue;
  if (n === 0) return emptyValue;

  if (Math.abs(n) >= 1_000_000) {
    const val = n / 1_000_000;
    return `${new Intl.NumberFormat(locale, { maximumFractionDigits: val % 1 === 0 ? 0 : 1 }).format(val)}M`;
  }
  if (Math.abs(n) >= 10_000) {
    const val = n / 1_000;
    return `${new Intl.NumberFormat(locale, { maximumFractionDigits: val % 1 === 0 ? 0 : 1 }).format(val)}k`;
  }
  return new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(n);
};

export const capitalize = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);

type NativeDownloadHandler = (blob: Blob, filename: string) => Promise<void>;
const DOWNLOAD_HANDLER_KEY = '__allie_native_download_handler__';

export const registerNativeDownloadHandler = (handler: NativeDownloadHandler): void => {
  (window as any)[DOWNLOAD_HANDLER_KEY] = handler;
};

export const downloadBlobFile = async (blob: Blob, filename: string): Promise<void> => {
  const nativeHandler = (window as any)[DOWNLOAD_HANDLER_KEY] as NativeDownloadHandler | undefined;
  if (nativeHandler) {
    try {
      await nativeHandler(blob, filename);
      return;
    } catch {
      // fall through to web fallback
    }
  }

  if (isMobile) {
    try {
      const mimeType = blob.type.split(';')[0] || 'application/octet-stream';
      const file = new File([blob], filename, { type: mimeType });
      if (
        typeof navigator.canShare === 'function' &&
        navigator.canShare({ files: [file] })
      ) {
        await navigator.share({ files: [file] });
        return;
      }
    } catch (err: any) {
      if (err?.name === 'AbortError') return;
    }
  }

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.target = '_blank';
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
};

export const secondsToHoursMinutes = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  
  if (hours === 0 && minutes === 0) {
    return '0m';
  }
  
  if (hours === 0) {
    return `${minutes}m`;
  }
  
  if (minutes === 0) {
    return `${hours}h`;
  }
  
  return `${hours}h ${minutes}m`;
};
